<template>
  <div class="table-container" v-if="allItems.length > 0">
    <b-row>
      <div class="col-xs-12 col-md-8 no-padding">
        <h3 class="table-title">
          {{ isTypeRecentPurchases ? $t('newLicenses') : $t('expiringTermLicenses') }}
        </h3>
        <span class="items-count">
          <template v-if="isTypeRecentPurchases">
            {{ $t('last30Days') }}
          </template>
          {{
            $t(
              'itemsCount',
              { number: allItems.length > 5 ? 5 : allItems.length, total }
            )
          }}
        </span>
      </div>
      <div class="col-xs-12 col-md-4 view-all-link">
        <a
          href="#"
          v-if="allItems.length > 5"
          @click.stop.prevent="handleViewAllClick"
        >
          {{
            isTypeRecentPurchases
              ? $t('viewAllNewPurchases')
              : $t('viewAllExpiringTermLicense')
          }}
        </a>
      </div>
    </b-row>
    <b-row :class="[tableHeaderClass, 'vertical-padding']" style="padding-left: 10px;">
      <div class="col checkbox-col" v-if="isTypeRecentPurchases">
        <b-checkbox @change="toggleAllLicensesSelection" v-model="areAllLicensesSelected" />
      </div>
      <div class="col">
        <b-row>
          <div :class="`col ${isTypeRecentPurchases ? 'indend-text' : ''}`">
            {{ $t('productName') }}
          </div>
          <div class="d-none d-md-block col dynamic-column-select-container header">
            {{ dynamicColumnHeader }}
            &nbsp;&nbsp;&nbsp;
            <i class="fa fa-caret-down" />
            <b-select
              :options="dynamicColumnOptions"
              v-model="dynamicColumnOption"
              class="dynamic-column-select"
            />
          </div>
          <template v-if="!isTypeRecentPurchases">
            <div class="d-none d-md-block col allocated-col text-right">
              {{ $t('allocated') }}
            </div>
          </template>
        </b-row>
      </div>
      <div class="col last-col header">
        {{ lastColumnHeaderText }}
      </div>
    </b-row>
    <template v-for="(item, i) in items">
      <b-row
        :key="item.ActivationID"
        :class="getTableRowClasses(item.ExpirationDate, i)"
      >
        <div class="col checkbox-col" v-if="isTypeRecentPurchases">
          <b-checkbox
            @change="toggleLicenseSelection(item)"
            :checked="isLicenseSelected(item)"
            :class="isLicenseInvalid(item) ? 'error-checkbox' : ''"
          />
        </div>
        <div class="col">
          <b-row>
            <div :class="`col ${isTypeRecentPurchases ? 'indend-text' : ''}`">
              <a
                :href="`/#/license/${item.ActivationID}`"
                :title="item.ProductName"
                @click.stop.prevent="handleLicenseLinkClick(item)"
              >
                {{ item.ProductName }}
              </a>
              <p v-if="isLicenseInvalid(item)" class="error-row-text">
                {{ $t('multipleSelectionHostTypeError', { hostType }) }}
              </p>
            </div>
            <div
              class="d-none d-md-block col dynamic-column-select-container"
              :title="dynamicColumnOption === 'HostValue' ? dynamicColumnItem(item) : ''"
            >
              {{ dynamicColumnItem(item) }}
            </div>
            <template v-if="!isTypeRecentPurchases">
              <div class="d-none d-md-block col allocated-col text-right">
                {{ getAllocatedText(item) }}
              </div>
            </template>
          </b-row>
        </div>
        <template>
          <div :class="getLastColumnClasses(item)">
            {{ getLastColumnItemText(item) }}
          </div>
        </template>
      </b-row>
    </template>
    <b-row>
      <div class="col-xs-12 col-md-6 cta-container no-padding">
        <b-button
          size="lg"
          variant="primary"
          class="cta-button"
          @click="handleAllocateClick()"
          :disabled="isCtaButtonDisabled || (isInternal && !isPersonInRole)"
          v-if="isTypeRecentPurchases"
          :title="selectedLicenses.length === 0 ? $t('noSelectionInfoText') : ''"
        >
          {{ $t('allocateLicenses') }}
        </b-button>
      </div>
    </b-row>
    <b-row>
      <div v-if="allItems.length > 0 && !isTypeRecentPurchases" class="col-12 no-padding">
        <p v-html="$t('expiringInfo', { customerServiceUrl: UrlBuilder.customerServiceUrl })"/>
      </div>
    </b-row>
    <allocation-modal
      :modal="allocationModal"
      :closeModal="closeAllocationModal"
      :selectedLicenses="selectedLicenses"
      :getUpdatedLicenses="getUpdatedLicenses"
    />
  </div>
</template>

<script>
  import Vue from 'vue'
  import moment from 'moment'
  import { mapGetters } from 'vuex'
  import AllocationModal from './AllocationModal'

  export default {
    name: 'LicenseTable',
    components: {
      'allocation-modal': AllocationModal
    },
    props: {
      isTypeRecentPurchases: {
        type: Boolean,
        default: false
      },
      allItems: {
        type: Array,
        required: true
      },
      total: {
        type: Number,
        default: null
      },
      getUpdatedLicenses: {
        type: Function,
        default: () => {}
      }
    },
    data () {
      return {
        isInternal: window.location.host.includes('citrite.net'),
        selectedLicenses: [],
        areAllLicensesSelected: false,
        hasError: false,
        isAllocateDisabled: false,
        dynamicColumnOption: 'SerialNumber',
        dynamicColumnOptions: [
          { value: 'SerialNumber', text: this.$t('licenseAccessCode') },
          { value: 'SAPSalesOrderNumber', text: this.$tc('orderNumber', 2) },
          { value: 'HostValue', text: this.$tc('hostId', 1) }
        ],
        allocationModal: false
      }
    },
    computed: {
      ...mapGetters(['isPersonInRole', 'showSurveyModal']),
      tableHeaderClass () {
        return this.isTypeRecentPurchases ? 'recent-purchases-header' : 'expiring-licenses-header'
      },
      items () {
        return [ ...this.allItems ].slice(0, 5)
      },
      viewAllButtonText () {
        return this.isTypeRecentPurchases
          ? 'View all new purchases link'
          : 'View all expiring term license link'
      },
      buttonText () {
        return this.isTypeRecentPurchases ? 'Allocate button' : 'Renew button'
      },
      helpButtonText () {
        return this.isTypeRecentPurchases ? 'Allocation help link' : 'Renewal help link'
      },
      isCtaButtonDisabled () {
        if (!this.isTypeRecentPurchases) {
          return false
        } else {
          return this.selectedLicenses.length === 0 || this.hasError || this.isAllocateDisabled
        }
      },
      dynamicColumnHeader () {
        return this.dynamicColumnOptions.find(o => o.value === this.dynamicColumnOption).text
      },
      lastColumnHeaderText () {
        return this.isTypeRecentPurchases
          ? this.$t('allocated')
          : this.$t('expires')
      },
      hostType () {
        return this.selectedLicenses.length > 0
          ? this.$t(this.selectedLicenses[0].HostType.toLowerCase())
          : ''
      }
    },
    methods: {
      calculateDayDifference (date, expiring = false) {
        let isDateLessThanToday, from, to, number
        number = expiring ? 0 : 1
        date = new Date(date)
        date = date.setTime(
          Date.UTC(
            date.getFullYear(), date.getMonth(), date.getDate(),
            date.getHours(), date.getMinutes(), date.getSeconds()
          )
        )
        isDateLessThanToday = Date.now() < new Date(date).getTime()
        if (moment().isSame(moment(date), 'day')) {
          return 0
        }
        if (expiring && !isDateLessThanToday) {
          return -1
        }
        from = isDateLessThanToday ? moment(date) : moment()
        to = isDateLessThanToday ? moment().subtract(1, 'days') : moment(date)
        return from.diff(to, 'days') + number
      },
      getTableRowClasses (expiryDate, i) {
        const classes = ['vertical-padding', i % 2 === 0 ? '' : 'odd-row']
        const dayDifference = this.calculateDayDifference(expiryDate, true)
        if (this.isTypeRecentPurchases) {
          return classes.concat(['recent-purchases-row'])
        }
        if (dayDifference <= 29) {
          return classes.concat(['expiring-licenses-row-less-days'])
        } else if (dayDifference > 29 && dayDifference <= 89) {
          return classes.concat(['expiring-licenses-row-more-days'])
        }
        return classes
      },
      getReadableExpiryDate (expiryDate) {
        const dayDifference = this.calculateDayDifference(expiryDate, true)
        return this.$tc('day', dayDifference, { count: dayDifference })
      },
      getTextColor (expiryDate) {
        const dayDifference = this.calculateDayDifference(expiryDate, true)
        if (dayDifference >= 0 && dayDifference <= 30) return 'danger-text'
        else if (dayDifference > 30 && dayDifference <= 90) return 'warning-text'
        return null
      },
      trackClick (linkType) {
        Vue.AppInsights.trackEvent(linkType, {
          eventType: 'click',
          page: 'Landing page'
        })
      },
      handleViewAllClick () {
        this.trackClick(this.viewAllButtonText)
        this.$router.push({
          name: 'AllLicenses',
          params: {
            landing: this.isTypeRecentPurchases ? 'newPurchases' : 'expiringLicenses'
          }
        })
      },
      validateMultipleSelection () {
        let hostType
        if (!this.selectedLicenses.length) return
        hostType = this.selectedLicenses[0].HostType
        this.selectedLicenses = this.selectedLicenses.map(l => {
          if (l.HostType === hostType) {
            delete l.isValid
            return l
          } else {
            return { ...l, isValid: false }
          }
        })
        this.hasError = this.selectedLicenses.some(l => l.hasOwnProperty('isValid'))
        this.isAllocateDisabled = this.selectedLicenses
          .some(l => l.RemainingUserCount === 0)
      },
      isLicenseInvalid (license) {
        return (this.isLicenseSelected(license, true) || {}).hasOwnProperty('isValid')
      },
      isLicenseSelected (license, returnItem = false) {
        const selectedLicense = this.selectedLicenses
          .find(val => val.ActivationID === license.ActivationID)
        return returnItem ? selectedLicense : !!selectedLicense
      },
      toggleAllLicensesSelection () {
        this.selectedLicenses = this.areAllLicensesSelected
          ? []
          : [...this.items]
        this.validateMultipleSelection()
      },
      toggleLicenseSelection (license) {
        if (!this.selectedLicenses.length || !this.isLicenseSelected(license)) {
          this.selectedLicenses.push(license)
        } else {
          this.selectedLicenses = this.selectedLicenses
            .filter(l => l.ActivationID !== license.ActivationID)
        }
        this.validateMultipleSelection()
      },
      handleAllocateClick () {
        this.trackClick(this.buttonText)
        if (this.isTypeRecentPurchases) {
          this.allocationModal = true
        }
      },
      closeAllocationModal () {
        this.allocationModal = false
      },
      handleLicenseLinkClick (license) {
        this.$store.commit('setLicenseDetails', {
          ProductName: license.ProductName,
          ExpirationDate: license.ExpirationDate,
          IsPermanent: license.IsPermanent,
          ActivationID: license.ActivationID,
          IsArchived: license.IsArchived,
          AllowpartialAllocation: license.AllowpartialAllocation
        })
        this.$router.push(`/license/${license.ActivationID}`)
      },
      dynamicColumnItem (license) {
        switch (this.dynamicColumnOption) {
          case 'SAPSalesOrderNumber':
            if (!license.LicenseItemNumber) {
              return `${license.SAPSalesOrderNumber}/${this.getLicenseItemNumber(license)}`
            }
            return `${license.SAPSalesOrderNumber}/${license.LicenseItemNumber}`
          case 'HostValue':
            return license.HostValue || ''
          case 'SerialNumber':
          default:
            return license.SerialNumber || ''
        }
      },
      getAllocatedText ({ RemainingUserCount, UserCount }) {
        return RemainingUserCount > UserCount
          ? `${UserCount} of ${UserCount}`
          : `${UserCount - RemainingUserCount} of ${UserCount}`
      },
      getLastColumnItemText (item) {
        return this.isTypeRecentPurchases
          ? this.getAllocatedText(item)
          : this.getReadableExpiryDate(item.ExpirationDate)
      },
      getLicenseItemNumber ({ ActivationID }) {
        return ActivationID.match(/^.*-.*-(.*)/)[1]
      },
      getLastColumnClasses ({ ExpirationDate }) {
        const classes = ['col', 'last-col']
        const dayDifference = this.calculateDayDifference(ExpirationDate, true)
        if (this.isTypeRecentPurchases) {
          classes.push('success-text')
        } else if (dayDifference >= 0 && dayDifference <= 30) {
          classes.push('danger-text')
        } else if (dayDifference > 30 && dayDifference <= 90) {
          classes.push('warning-text')
        }
        return classes
      }
    }
  }
</script>

<style lang="scss">
  @import '../styles/variables.scss';

  .table-container {
    padding-top: 20px;
  }
  .table-title {
    display: inline-block;
  }
  .items-count {
    display: block;
    color: $darkGray;
  }
  .view-all-link,
  .help-link {
    margin: 10px 0;
  }
  .recent-purchases-header,
  .expiring-licenses-header {
    color: $white;
    margin-bottom: 10px;
  }
  .recent-purchases-header {
    background-color: $success;
    border-left-width: 10px;
  }
  .expiring-licenses-header {
    background-color: $danger;
  }
  .indend-text {
    text-indent: -10px;
  }
  .vertical-padding {
    padding-top: 7px;
    padding-bottom: 7px;
  }
  .odd-row {
    background-color: $lightPrimary;
  }
  .recent-purchases-row,
  .expiring-licenses-row-less-days,
  .expiring-licenses-row-more-days {
    border-radius: 5px;
    margin-bottom: 10px;
  }
  .recent-purchases-row {
    border: 0.5px solid $lightGray;
    border-left-width: 10px;
    border-left-color: $success;
  }
  .expiring-licenses-row-less-days {
    border: 0.5px solid $lightGray;
    border-left-width: 10px;
    border-left-color: $danger;
  }
  .expiring-licenses-row-more-days {
    border: 0.5px solid $lightGray;
    border-left-width: 10px;
    border-left-color: $warning;
  }
  .primary-text {
    color: $primary;
  }
  .success-text {
    color: $success;
  }
  .danger-text {
    color: $danger;
  }
  .warning-text {
    color: $warning;
  }
  .cta-container {
    text-align: center;
  }
  .cta-button {
    margin: 10px 0;
  }
  .help-link {
    text-align: center;
  }
  .custom-control-inline {
    margin-right: 0;
    padding: 3px;
  }
  .checkbox-col {
    flex: 0 0 16px;
  }
  .error-row-text {
    color: $danger;
    white-space: normal;
  }
  .dynamic-column-select-container {
    position: relative;
    flex: 0 0 340px;
    max-width: 340px;
  }
  .dynamic-column-select-container:not(.header) {
    overflow-x: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .dynamic-column-select {
    opacity: 0;
    position: absolute;
    left: 0;
    top: 1px;
    cursor: pointer;
  }
  .allocated-col {
    flex: 0 0 150px;
  }
  .last-col {
    flex: 0 0 150px;
    text-align: right;
  }
  .last-col:not(.header) {
    font-weight: bold;
  }
  .no-padding {
    padding: 0;
  }
  @media (min-width: 768px) {
    .items-count {
      display: inline;
    }
    .view-all-link,
    .help-link {
      text-align: right;
    }
    .cta-container {
      text-align: initial;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/LicenseTable.vue